import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { startSimulation } from "@/lib/api";
import { useAuthStore } from "@/lib/authStore";
import { SCENARIO_BRIEFS, PERSONA_META, DISCLAIMER_TEXT } from "@/lib/personaCatalog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowRight, BarChart3, User, LogIn, AlertTriangle, ShieldCheck, Cpu,
  Layers, BookOpenCheck, ChevronDown, Target, Clock, Radar, Landmark,
} from "lucide-react";

const NAVY = "#002856";
const BLUE = "#009CDE";
const ORANGE = "#FF9933";
const MONO = { fontFamily: "'Roboto Mono', monospace" };

const AkamaiLogo = ({ height = 28 }) => (
  <span className="inline-flex items-center bg-white rounded-md px-2.5 py-1 shadow-sm border border-slate-100" data-testid="akamai-logo">
    <img src="/assets/akamai-logo.png" alt="Akamai" style={{ height }} />
  </span>
);

const PlatypaiLockup = () => (
  <span className="inline-flex items-center gap-2 text-sm" data-testid="platypai-lockup">
    <span style={{ color: "#54565B" }}>Powered by</span>
    <img src="/assets/platypai-logo.webp" alt="PlatypAI" style={{ height: 24 }} />
    <span style={{ color: "#54565B" }}>· Deal Velocity platform</span>
  </span>
);

const TRACKS = [
  {
    id: "sdr", title: "SDR Edition", tag: "foundational", icon: Radar,
    hook: "Earn the next conversation.",
    summary: "Ten minutes with an overloaded Head of AppSec. Qualify the pain, win the technical ally, land a capped 6-week assessment.",
    deal: "6-week API Discovery Assessment · ~₹1.3 cr, capped", turns: "30 turns max",
    detail: "The learner is an Akamai SDR with ten minutes of a busy Head of Application Security's attention. Three personas, gentler trust thresholds, shorter sessions, and coaching tuned to prospecting craft: research-led relevance, sharp questions, small commitments.",
  },
  {
    id: "sales", title: "Sales Edition", tag: "advanced", icon: Landmark,
    hook: "Sell a governance outcome to a board deadline.",
    summary: "Full cycle with a Group CISO carrying a 90-day board mandate — through engineering, procurement, and the regulator's voice.",
    deal: "Multi-entity platform subscription · ₹8–15 cr/yr", turns: "40 turns max",
    detail: "The learner is a Major Account Executive running the full cycle with the economic buyer. Four personas including a procurement blocker and a risk influencer, a multi-entity platform deal, default trust thresholds, and scoring weighted toward the qualification work that wins or loses enterprise security deals.",
  },
];

const HUB_TABS = [
  {
    key: "prototype", title: "The Prototype", icon: ShieldCheck, illus: null,
    summary: "A working Deal Velocity build for Akamai's security sales org: sell into a fictionalized Jio Financial Services (25M users, four regulators) that can't count its own APIs.",
    full: "PlatypAI has adapted its Deal Velocity simulation platform into a working prototype for Akamai's security sales organization. The scenario: Jio Financial Services, a 25M-user digital finance group answering to four regulators, has discovered it cannot count its own APIs, and Akamai's job is to turn that problem into a deal. Two role editions ship in this build, one for SDRs and one for Account Executives, drawn from the same researched knowledge base. Everything a learner meets in the simulation, from the buyer's org chart to the procurement objections, was authored to Akamai's ingestion specification and validated against it.",
  },
  {
    key: "offer", title: "How It Works", icon: Cpu, illus: "/assets/illus/meddpicc_radar.png",
    summary: "AI personas that hold character · deterministic MEDDPICC scoring · per-persona trust meter · 4 gated phases · a live coach · a Deal Velocity Index report at the end.",
    full: "Reps rehearse the conversations that decide deals: discovery with a guarded CISO, technical validation with an engineer who distrusts vendors, commercial combat with group procurement. AI personas hold character, remember the conversation, and only know what the scenario gives them — a learner cannot talk their way past discovery with charm. Under the dialogue sits deterministic scoring: every message is scanned for evidence against MEDDPICC, a per-persona trust meter moves on defined behavioral triggers, and the conversation advances through four gated phases. A coach intervenes when the learner stalls. At the end, a Deal Velocity Index report shows exactly which qualification dimensions were earned, which were skipped, and what the trust curve looked like turn by turn. Repeatable, self-paced, and safe to fail in — the expensive mistakes happen here, not at the account.",
  },
  {
    key: "architecture", title: "Role Architecture", icon: Layers, illus: "/assets/illus/persona_network.png",
    summary: "One scenario (akamai_jfs), two role profiles. Shared facts and hidden problem; role-scoped personas, trust calibration, and coaching.",
    full: "Both editions run as role profiles on a single akamai_jfs scenario: one shared knowledge base, with role-scoped framing, personas, trust calibration, and coaching. What changes is the job the learner is doing — the same buyer world, the same regulatory pressure, the same hidden problem buried for good discovery to find. Future roles (a Solutions Engineer edition is the obvious next one) are a role definition, not a rebuild.",
  },
  {
    key: "provenance", title: "Fact Provenance", icon: BookOpenCheck, illus: "/assets/illus/provenance_nodes.png",
    summary: "Verified public facts (JFS results, RBI/SEBI/DPDP/CERT-In obligations, Akamai's published capabilities) + a clearly labeled fictional layer — documented separately.",
    full: "The simulation's credibility comes from research, not invention. The buyer world is grounded in Jio Financial Services' published results and verified public facts: scale, business lines, leadership structure, and the regulatory obligations that make API security urgent. The Akamai side is grounded the same way: product capabilities, analyst recognition, published pricing anchors, and India presence, sourced from Akamai's own public materials. Every real-world claim maps to a cited source in the build's provenance appendix. On top of that verified base sits a clearly labeled fictional layer: invented personas in realistic roles, an invented crisis with internally consistent numbers, and invented deal economics calibrated against Akamai's published entry pricing. The two layers are documented separately so reviewers can check the facts and edit the fiction independently. A four-lens QA review has verified both.",
  },
];

const RoleCard = ({ track, starting, onStart }) => {
  const brief = SCENARIO_BRIEFS[track.id];
  const Icon = track.icon;
  return (
    <article
      className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col min-w-0 transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-[#009CDE]"
      style={{ borderTop: `4px solid ${ORANGE}` }}
      data-testid={`scenario-card-${track.id}`}
      aria-labelledby={`track-title-${track.id}`}
    >
      <div className="p-5 flex flex-col flex-1">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(0,156,222,0.1)" }} aria-hidden="true">
              <Icon className="w-4.5 h-4.5" size={18} style={{ color: BLUE }} />
            </span>
            <h3 id={`track-title-${track.id}`} className="text-lg font-bold" style={{ color: NAVY }}>{track.title}</h3>
          </div>
          <span className="text-[11px] text-slate-500 break-words sm:text-right sm:shrink-0" style={MONO} data-testid={`scenario-id-${track.id}`}>
            akamai_jfs · role: {track.id} · {track.tag}
          </span>
        </div>

        <p className="text-sm font-medium mt-3" style={{ color: BLUE }}>{track.hook}</p>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{track.summary}</p>

        <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <div className="flex items-start gap-1.5 min-w-0"><Target size={13} className="shrink-0 mt-0.5" style={{ color: ORANGE }} aria-hidden="true" /><dt className="sr-only">Deal</dt><dd className="min-w-0">{track.deal}</dd></div>
          <div className="flex items-start gap-1.5 min-w-0"><Clock size={13} className="shrink-0 mt-0.5" aria-hidden="true" /><dt className="sr-only">Length</dt><dd className="min-w-0">{track.turns} · text or voice</dd></div>
        </dl>

        <ul className="mt-4 space-y-1.5 list-none" aria-label={`${track.title} stakeholders`}>
          <li className="text-[10px] uppercase tracking-wider text-slate-400" style={MONO} aria-hidden="true">You'll face</li>
          {brief.personas.map((pid) => {
            const p = PERSONA_META[pid];
            return (
              <li key={pid}>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center gap-2.5 rounded-lg border border-slate-200 px-2 py-1.5 text-left hover:border-[#009CDE] hover:bg-[#f4fafd] transition-colors focus-visible:outline-2 focus-visible:outline-[#009CDE]"
                      aria-haspopup="dialog"
                      aria-label={`View details for ${p.name}, ${p.role}`}
                      data-testid={`persona-chip-${pid}`}
                    >
                      <span className="w-7 h-7 shrink-0 rounded-full text-[10px] font-semibold flex items-center justify-center text-white" style={{ backgroundColor: NAVY }} aria-hidden="true">{p.short}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold text-slate-800 truncate">{p.name}</span>
                        <span className="block text-[11px] text-slate-500 truncate">{p.role}</span>
                      </span>
                      <ChevronDown size={13} className="shrink-0 text-slate-400 -rotate-90" aria-hidden="true" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 text-sm" align="start" data-testid={`persona-detail-${pid}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-bold" style={{ color: NAVY }}>{p.name}</p>
                        <p className="text-xs text-slate-500">{p.role}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] shrink-0 border-slate-300 text-slate-600">{p.difficulty}</Badge>
                    </div>
                    <p className="text-[11px] font-medium mt-2" style={{ color: BLUE }}>{p.archetype}</p>
                    <p className="text-xs text-slate-600 leading-relaxed mt-1.5">{p.background}</p>
                    <div className="mt-2.5 grid grid-cols-1 gap-1.5">
                      <div className="flex flex-wrap gap-1" aria-label="Builds trust">
                        {p.positive_triggers.map((t) => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">+ {t}</span>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-1" aria-label="Damages trust">
                        {p.negative_triggers.map((t) => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">− {t}</span>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </li>
            );
          })}
        </ul>

        <Collapsible className="mt-3">
          <CollapsibleTrigger className="group flex items-center gap-1 text-xs font-medium focus-visible:outline-2 focus-visible:outline-[#009CDE] rounded" style={{ color: BLUE }} data-testid={`track-detail-toggle-${track.id}`}>
            More about this track
            <ChevronDown size={13} className="transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid={`track-detail-${track.id}`}>{track.detail}</p>
          </CollapsibleContent>
        </Collapsible>

        <Button
          className="mt-4 w-full text-white font-medium"
          style={{ backgroundColor: BLUE }}
          onClick={onStart}
          disabled={!!starting}
          data-testid={`start-scenario-btn-${track.id}`}
        >
          {starting === track.id ? "Starting..." : (<>Start Simulation <ArrowRight size={15} className="ml-1.5" aria-hidden="true" /></>)}
        </Button>
      </div>
    </article>
  );
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [starting, setStarting] = useState(null);
  const [confirmTrack, setConfirmTrack] = useState(null);

  const handleStart = async (roleId) => {
    setStarting(roleId);
    try {
      let response;
      try {
        response = await startSimulation("akamai_jfs", roleId);
      } catch (first) {
        await new Promise((r) => setTimeout(r, 1500));
        response = await startSimulation("akamai_jfs", roleId);
      }
      navigate(`/simulation/${response.data.session_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? `Could not start: ${detail}` : "Failed to start simulation — the server may be waking up. Please try again in a moment.");
      setStarting(null);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10" style={{ backgroundColor: "rgba(0,40,86,0.97)", backdropFilter: "blur(8px)" }}>
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AkamaiLogo height={24} />
            <span className="text-white/40 text-sm hidden sm:inline" aria-hidden="true">|</span>
            <span className="text-white/80 text-sm hidden sm:inline">Deal Velocity</span>
          </div>
          <nav className="flex items-center gap-1" aria-label="Main navigation">
            <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10" onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn">
              <BarChart3 size={15} className="mr-1.5" aria-hidden="true" /> Dashboard
            </Button>
            {user ? (
              <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10" onClick={() => navigate("/profile")} data-testid="nav-profile-btn">
                <User size={15} className="mr-1.5" aria-hidden="true" /> {user.name || "Profile"}
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10" onClick={() => navigate("/login")} data-testid="nav-login-btn">
                <LogIn size={15} className="mr-1.5" aria-hidden="true" /> Sign in
              </Button>
            )}
          </nav>
        </div>
      </header>

      {/* Hero — split, tight */}
      <section style={{ backgroundColor: NAVY }} data-testid="hero-section" aria-labelledby="hero-title">
        <div className="max-w-5xl mx-auto px-6 pt-10 pb-12 grid md:grid-cols-[3fr_2fr] gap-8 items-center">
          <div>
            <h1 id="hero-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" style={{ lineHeight: 1.15 }}>
              Practice the Jio Financial Services deal before anyone makes a real call.
            </h1>
            <div className="w-20 h-1 my-4 rounded-full" style={{ backgroundColor: BLUE }} aria-hidden="true" />
            <p className="text-sm sm:text-base text-white/85 leading-relaxed max-w-xl">
              An AI sales simulation built for Akamai by PlatypAI. Sell Akamai API Security to a fictionalized
              JFS buying group — and get scored the way the deal would actually be scored.
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              <Button className="text-white font-medium" style={{ backgroundColor: BLUE }} onClick={() => setConfirmTrack(TRACKS[0])} data-testid="hero-start-sdr-btn">
                Start SDR Edition <ArrowRight size={15} className="ml-1.5" aria-hidden="true" />
              </Button>
              <Button variant="outline" className="bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white font-medium" onClick={() => setConfirmTrack(TRACKS[1])} data-testid="hero-start-sales-btn">
                Start Sales Edition
              </Button>
            </div>
            <p className="mt-4 text-[11px] text-white/50 italic" data-testid="hero-fiction-note">
              A working prototype for Akamai enablement review. Personas are fictitious; the crisis narrative is invented for training.
            </p>
          </div>
          <img
            src="/assets/illus/hero_api_mesh.png"
            alt="Outline illustration of an API security shield inside a mesh of connected service nodes"
            className="hidden md:block w-full max-w-[300px] justify-self-center select-none"
            loading="eager"
          />
        </div>
      </section>

      {/* Stat strip */}
      <section style={{ backgroundColor: "#001D40" }} data-testid="stat-strip" aria-label="Simulation at a glance">
        <div className="max-w-5xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[["7", "AI personas"], ["2", "role editions"], ["4", "conversation phases"], ["8", "MEDDPICC dimensions scored"]].map(([n, label]) => (
            <div key={label}>
              <div className="text-2xl sm:text-3xl font-extrabold" style={{ ...MONO, color: ORANGE }}>{n}</div>
              <div className="text-[11px] sm:text-xs text-white/75 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Role cards */}
      <section className="bg-white" data-testid="tracks-section" aria-labelledby="tracks-title">
        <div className="max-w-5xl mx-auto px-6 pt-10 pb-8">
          <h2 id="tracks-title" className="text-xl sm:text-2xl font-bold tracking-tight" style={{ color: NAVY }}>Choose your track</h2>
          <div className="flex items-center gap-3 mt-2 mb-5">
            <div className="flex-1 h-px bg-slate-200" aria-hidden="true" />
            <span className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-500 border border-slate-200 rounded-full bg-white" style={MONO} data-testid="knowledge-base-bar">
              one knowledge base · akamai_jfs
            </span>
            <div className="flex-1 h-px bg-slate-200" aria-hidden="true" />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {TRACKS.map((track) => (
              <RoleCard key={track.id} track={track} starting={starting} onStart={() => setConfirmTrack(track)} />
            ))}
          </div>
        </div>
      </section>

      {/* Progressive disclosure hub */}
      <section style={{ backgroundColor: "#F8FAFC" }} data-testid="prototype-section" aria-labelledby="hub-title">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <h2 id="hub-title" className="text-xl sm:text-2xl font-bold tracking-tight mb-1" style={{ color: NAVY }}>Inside the build</h2>
          <p className="text-sm text-slate-500 mb-5">Four things reviewers ask about — the short version first, full briefs on click.</p>
          <Tabs defaultValue="prototype" data-testid="offer-section">
            <TabsList className="flex flex-wrap h-auto gap-1 bg-white border border-slate-200 p-1 rounded-lg justify-start">
              {HUB_TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <TabsTrigger
                    key={t.key} value={t.key}
                    className="gap-1.5 text-xs sm:text-sm rounded-md px-3 py-1.5 data-[state=active]:shadow-none data-[state=active]:text-white data-[state=active]:bg-[#002856]"
                    data-testid={`hub-tab-${t.key}`}
                  >
                    <Icon size={14} aria-hidden="true" /> {t.title}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {HUB_TABS.map((t) => (
              <TabsContent key={t.key} value={t.key} className="mt-4" data-testid={t.key === "provenance" ? "provenance-section" : undefined}>
                <div className="bg-white border border-slate-200 rounded-xl p-5 grid md:grid-cols-[1fr_140px] gap-5 items-start">
                  <div>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed font-medium">{t.summary}</p>
                    <Collapsible className="mt-3">
                      <CollapsibleTrigger className="group inline-flex items-center gap-1 text-xs font-semibold focus-visible:outline-2 focus-visible:outline-[#009CDE] rounded" style={{ color: BLUE }} data-testid={`hub-expand-${t.key}`}>
                        Read the full brief
                        <ChevronDown size={13} className="transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <p className="text-sm text-slate-600 leading-relaxed mt-3 pt-3 border-t border-slate-100" data-testid={`hub-full-${t.key}`}>{t.full}</p>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                  {t.illus && (
                    <img src={t.illus} alt="" aria-hidden="true" className="hidden md:block w-[140px] select-none" loading="lazy" />
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: "#e7eef3" }}>
        <div className="max-w-5xl mx-auto px-6 py-5 flex flex-col gap-1.5">
          <PlatypaiLockup />
          <p className="text-[11px] italic" style={{ color: "#54565B" }} data-testid="footer-disclaimer">{DISCLAIMER_TEXT}</p>
        </div>
      </footer>

      {/* CR-003 disclaimer dialog */}
      <Dialog open={!!confirmTrack} onOpenChange={(o) => !o && setConfirmTrack(null)}>
        <DialogContent data-testid="disclaimer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={19} style={{ color: ORANGE }} aria-hidden="true" />
              Fictional training scenario
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed">{DISCLAIMER_TEXT}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmTrack(null)} data-testid="disclaimer-cancel-btn">Cancel</Button>
            <Button className="text-white" style={{ backgroundColor: BLUE }} onClick={() => { const id = confirmTrack.id; setConfirmTrack(null); handleStart(id); }} data-testid="disclaimer-accept-btn">
              I understand — start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
